/**
 * Shared lot ordering + filtering for the sale grid.
 *
 * Consumed by BOTH the single-sale page (SalePage) and the homepage
 * (PublicAuctionList) so the two can never drift. Pure functions only — the
 * toggle STATE stays local to each page; this module just derives the view.
 *
 * Rules (locked):
 *  - Default order: numeric lotNumber ascending. lotNumber is stored as a STRING,
 *    so parse it — "10" must not sort before "9". Missing/non-numeric sorts LAST.
 *  - Category grouping (optional): alphabetical by category, lotNumber ascending
 *    within each group. Blank/missing category is grouped LAST.
 *  - No-bids filter (optional): uses bidCount (0 or missing = no bids). NEVER
 *    currentBid — the importer seeds currentBid = startingBid on every lot, so it
 *    is not a reliable "has a bid" signal.
 *  - TIER ORDER (opt-in, self-gating): when ANY lot in the list carries a `tier`,
 *    the list orders by tier first — chaser, star, rookie, common — and falls
 *    back to the lot-number rule within each tier. When NO lot has a tier, which
 *    is every sale but a tiered reveal, ordering is byte-for-byte what it was.
 *    Nothing to switch on and nothing to remember.
 *
 *    Why tier and not lot number: on the joe104 bounty the lot number IS the sku
 *    (701722), because the sku is the identity that bridges a bounty to the
 *    reveal. Identity therefore cannot also carry presentation, so the running
 *    order needs its own field.
 */

/** Minimal shape this module needs; AuctionLot (and the homepage's `any` lots) satisfy it. */
export interface DisplayableLot {
  lotNumber?: string;
  category?: string;
  bidCount?: number;
  /** Optional segment tier — see TIER ORDER below. Absent on almost every sale. */
  tier?: string;
}

export interface LotDisplayOptions {
  groupByCategory: boolean;
  noBidsOnly: boolean;
}

export interface LotDisplayResult<T> {
  /** Filtered + sorted lots ready to render. */
  lots: T[];
  /** How many are shown after filtering. */
  shownCount: number;
  /** Total before filtering (for "Showing X of Y lots"). */
  totalCount: number;
}

/**
 * Coerce ANY raw field value to a trimmed string. Firestore lot data is not guaranteed
 * to match the TS types — `category` has arrived as a number/null in the live sale, and
 * calling .trim()/.localeCompare() on a non-string throws and blanks the whole page.
 * Never call a string method on a raw field; route it through here first.
 */
const asTrimmedString = (v: unknown): string => (v == null ? '' : String(v)).trim();

/**
 * The ONE lot-number representation in the app. Use this everywhere a lot number is
 * shown to a bidder — tiles, detail header, breadcrumbs — so the grid and the lot page
 * can never disagree about what lot 7 is called.
 *
 * Format is "LOT 7", matching what the lot detail page has always rendered. lotNumber is
 * stored as a STRING and is not guaranteed present, so it is coerced (see asTrimmedString)
 * and a missing one renders "LOT —" rather than "LOT undefined".
 */
export const lotNumberLabel = (lot: { lotNumber?: unknown }): string =>
  `LOT ${asTrimmedString(lot.lotNumber) || '—'}`;

/** Category label for grouping and section headers — blank/missing => 'Uncategorized'. */
export const lotCategoryLabel = (lot: { category?: unknown }): string =>
  asTrimmedString(lot.category) || 'Uncategorized';

/** No bids = bidCount 0, missing, or non-numeric. Deliberately NOT based on currentBid. */
export const lotHasNoBids = (lot: { bidCount?: unknown }): boolean => {
  const n = Number(lot.bidCount);
  return !Number.isFinite(n) || n === 0;
};

/**
 * Tier running order. Unknown tiers sort after the four known ones but still
 * ahead of untiered lots, so a typo'd tier is visible rather than silently last.
 */
const TIER_RANK: Record<string, number> = { chaser: 0, star: 1, rookie: 2, common: 3 };
const UNTIERED = 99;

const tierRank = (lot: { tier?: unknown }): number => {
  const t = asTrimmedString(lot.tier).toLowerCase();
  if (!t) return UNTIERED;
  const r = TIER_RANK[t];
  return r === undefined ? UNTIERED - 1 : r;
};

/** Numeric lot number; missing/non-numeric sorts LAST. Coerces before parsing. */
const lotNumberValue = (lot: { lotNumber?: unknown }): number => {
  const n = parseInt(asTrimmedString(lot.lotNumber), 10);
  return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
};

/** Alphabetical category compare; blank/missing category ordered LAST. All coerced first. */
const categoryCompare = (a: { category?: unknown }, b: { category?: unknown }): number => {
  const sa = asTrimmedString(a.category);
  const sb = asTrimmedString(b.category);
  if (!sa && !sb) return 0;
  if (!sa) return 1;
  if (!sb) return -1;
  return sa.localeCompare(sb);
};

/**
 * Derive the display list. Pure — never mutates the input array (sorts a copy).
 */
export function computeLotDisplay<T extends DisplayableLot>(
  lots: T[],
  opts: LotDisplayOptions,
): LotDisplayResult<T> {
  const totalCount = lots.length;
  const filtered = (opts.noBidsOnly ? lots.filter(lotHasNoBids) : lots.slice());
  // Self-gating: computed once, outside the comparator. No lot carries a tier on
  // an ordinary sale, so `useTier` is false and the sort below is unchanged.
  const useTier = filtered.some((l) => tierRank(l) !== UNTIERED);
  try {
    filtered.sort((a, b) => {
      if (opts.groupByCategory) {
        const c = categoryCompare(a, b);
        if (c !== 0) return c;
      }
      if (useTier) {
        const t = tierRank(a) - tierRank(b);
        if (t !== 0) return t;
      }
      return lotNumberValue(a) - lotNumberValue(b);
    });
  } catch (err) {
    // Belt-and-suspenders: the coercion above should prevent any throw, but a single
    // malformed lot must NEVER blank the page — fall back to the filtered, unsorted list.
    console.error('computeLotDisplay: sort failed, rendering unsorted', err);
  }
  return { lots: filtered, shownCount: filtered.length, totalCount };
}
